import React, { useState, useEffect } from 'react';
import { ApiPortfolioSummary, COMMENT_CATEGORIES } from '@/types';
import { useComments } from '@/contexts/CommentsContext';
import { useAuth } from '@/contexts/AuthContext';
import LCIcon from '@/components/common/LCIcon';
import { LCBtn, CLS } from '@/components/common/LCPrimitives';

interface CommentsSidebarProps {
  portfolio: ApiPortfolioSummary;
  onClose: () => void;
}

function timeAgo(ts: Date | string): string {
  const m = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const CommentsSidebar: React.FC<CommentsSidebarProps> = ({ portfolio, onClose }) => {
  const { user } = useAuth();
  const { getCommentsForPortfolio, addComment, addReply, fetchComments, isLoading } = useComments();
  const [draft, setDraft] = useState('');
  const [category, setCategory] = useState('Other');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const pid = portfolio.portfolio_id_lig;

  useEffect(() => {
    fetchComments(pid);
  }, [pid, fetchComments]);

  const allComments = getCommentsForPortfolio(pid);
  const loading = isLoading[pid] || false;

  const organized = allComments
    .filter(c => !c.parentId)
    .map(c => ({ ...c, replies: allComments.filter(r => r.parentId === c.id) }))
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const handleSubmit = async () => {
    if (!draft.trim() || submitting) return;
    setSubmitting(true);
    try {
      await addComment(pid, draft.trim(), category);
      setDraft('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (parentId: string) => {
    if (!replyText.trim() || submitting) return;
    setSubmitting(true);
    try {
      await addReply(pid, parentId, replyText.trim());
      setReplyText('');
      setReplyingTo(null);
    } finally {
      setSubmitting(false);
    }
  };

  const currentUserName = user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name}`
    : user?.email?.split('@')[0] || 'You';

  return (
    <div className="fixed inset-0 bg-black/[0.36] flex justify-end z-[50] animate-fadein" onClick={onClose}>
      <div className="w-[460px] max-w-full bg-white h-full flex flex-col animate-slidein" onClick={e => e.stopPropagation()}>
        {/* Head */}
        <div className="px-[22px] py-[18px] border-b border-black/[0.08] flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#E50025] mb-1.5">Portfolio Comments</div>
            <h3 className="font-serif text-[22px] font-normal my-1 tracking-[-0.005em]">{portfolio.client_name}</h3>
            <div className="text-[12px] text-[#7A7A76] font-mono">{pid} · {organized.length} comment{organized.length !== 1 ? 's' : ''}</div>
          </div>
          <button className={CLS.iconBtn} onClick={onClose}><LCIcon name="close" size={16} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-[22px] py-4 flex flex-col gap-3.5">
          {loading && (
            <div className={`${CLS.muted} text-center py-7`}>Loading comments…</div>
          )}
          {!loading && organized.length === 0 && (
            <div className={`${CLS.muted} text-center py-7`}>No comments yet. Be the first to leave one.</div>
          )}
          {organized.map(c => (
            <div key={c.id}>
              <div className={`border-l-2 px-3 py-2 pb-2.5 bg-[#FAF9F8] ${c.userName === currentUserName ? 'border-l-black' : 'border-l-[#E50025]'}`}>
                <div className="flex justify-between gap-2 text-[10.5px] text-[#7A7A76] tracking-[0.04em] uppercase">
                  <span className="font-bold text-[#333] tracking-[0.06em]">{c.userName} · {c.userRole}</span>
                  <span>{timeAgo(c.timestamp)}</span>
                </div>
                <div className="mt-1.5 text-[13px] leading-[1.5] text-black">{c.content}</div>
                <div className="mt-2">
                  <LCBtn variant="ghost" size="sm" style={{ fontSize: 10, padding: '4px 8px' }} onClick={() => setReplyingTo(replyingTo === c.id ? null : c.id)}>
                    Reply
                  </LCBtn>
                </div>
              </div>
              {c.replies.map(r => (
                <div key={r.id} className={`border-l-2 px-3 py-2 pb-2.5 bg-[#FAF9F8] ml-4 mt-1 ${r.userName === currentUserName ? 'border-l-black' : 'border-l-[#E50025]'}`}>
                  <div className="flex justify-between gap-2 text-[10.5px] text-[#7A7A76] tracking-[0.04em] uppercase">
                    <span className="font-bold text-[#333] tracking-[0.06em]">{r.userName} · {r.userRole}</span>
                    <span>{timeAgo(r.timestamp)}</span>
                  </div>
                  <div className="mt-1.5 text-[13px] leading-[1.5] text-black">{r.content}</div>
                </div>
              ))}
              {replyingTo === c.id && (
                <div className="ml-4 mt-1.5 flex flex-col gap-1.5">
                  <textarea
                    placeholder="Write a reply…"
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    className="w-full min-h-[60px] px-2.5 py-2 border border-black/[0.16] text-[13px] resize-y focus:outline-none focus:border-[#E50025] font-[inherit]"
                  />
                  <div className="flex gap-2">
                    <LCBtn variant="primary" size="sm" onClick={() => handleReply(c.id)} disabled={submitting}>Post Reply</LCBtn>
                    <LCBtn variant="ghost" size="sm" onClick={() => setReplyingTo(null)}>Cancel</LCBtn>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-black/[0.08] px-[22px] py-3.5 flex flex-col gap-2.5">
          <div className="flex gap-2 mb-1.5">
            <select
              className={CLS.input}
              style={{ width: 160, padding: '6px 10px', fontSize: 12 }}
              value={category}
              onChange={e => setCategory(e.target.value)}
            >
              {COMMENT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <textarea
            className="resize-y min-h-[78px] px-3 py-2.5 border border-black/[0.16] font-[inherit] text-[13px] focus:outline-none focus:border-[#E50025]"
            placeholder={`Leave a comment as ${currentUserName}…`}
            value={draft}
            onChange={e => setDraft(e.target.value)}
          />
          <div className="flex justify-between items-center">
            <span className={`${CLS.muted} text-[11px]`}>Visible to all RMs and ICs.</span>
            <LCBtn variant="primary" size="sm" onClick={handleSubmit} disabled={submitting || !draft.trim()}>
              Post Comment <LCIcon name="arrow" size={12} />
            </LCBtn>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentsSidebar;
